'use client';

import { useIntl } from 'react-intl';
import { useState } from 'react';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { useAddCustomerReviewAllowed } from '../useAddCustomerReviewAllowed/useAddCustomerReviewAllowed';
import { AddReviewCallToAction } from '../AddReviewCallToAction/AddReviewCallToAction.client';
import { useCustomerReviewsTracking } from '../Tracking/useCustomerReviewsTracking/useCustomerReviewsTracking';
import { addReviewMessages } from './AddReview.messages';
import type { MultiStepFormProps } from './MultiStepForm/MultiStepForm.client';
import { DynamicMultiStepForm } from './DynamicMultiStepForm/DynamicMultiStepForm';

export type AddReviewProps = Pick<MultiStepFormProps, 'sku' | 'akl'>;

/**
 * Add customer review call to action button with multi step form in a dialog
 */
export const AddReview = ({ sku, akl }: AddReviewProps) => {
  const { formatMessage } = useIntl();
  const { trackCustomerReviewDialogOpened } = useCustomerReviewsTracking();
  const addCustomerReviewAllowed = useAddCustomerReviewAllowed();
  const { addNotification } = useNotification();

  const [dialogHeadline, setDialogHeadline] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!addCustomerReviewAllowed) return null;

  const handleDialogOpen = () => {
    trackCustomerReviewDialogOpened();
    setDialogHeadline(formatMessage(addReviewMessages.dialogTitle));
  };
  const handleDialogClose = () => {
    setDialogHeadline(null);
  };

  const handleSuccess = () => {
    addNotification({
      severity: 'success',
      duration: 5000,
      message: formatMessage(addReviewMessages.successNotification),
    });
    setSuccess(true);
    handleDialogClose();
  };

  const handleError = () => {
    addNotification({
      severity: 'error',
      duration: 5000,
      message: formatMessage(addReviewMessages.errorNotification),
    });
    handleDialogClose();
  };

  return (
    <div>
      {success && (
        <Notification severity="info" onClose={() => setSuccess(false)}>
          {formatMessage(addReviewMessages.successHint)}
        </Notification>
      )}
      <AddReviewCallToAction onAction={handleDialogOpen} />
      {dialogHeadline !== null && (
        <Dialog isOpen title={dialogHeadline} onClose={handleDialogClose}>
          <DialogHeader onClose={handleDialogClose}>{dialogHeadline}</DialogHeader>
          <DialogContent>
            <DynamicMultiStepForm
              sku={sku}
              akl={akl}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
